import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import AppFooter from '../components/AppFooter';
import TestimonyShareCard from '../components/TestimonyShareCard';
import {
  Shield, ArrowLeft, Heart, BookOpen, Send, MessageSquare, Sparkles, Flag, Share2,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_VERSES = [
  { verse: "The Lord is my shepherd; I shall not want.", ref: "Psalm 23:1" },
  { verse: "I can do all things through Christ who strengthens me.", ref: "Philippians 4:13" },
  { verse: "For God has not given us a spirit of fear, but of power and of love and of a sound mind.", ref: "2 Timothy 1:7" },
  { verse: "The Lord is close to the brokenhearted and saves those who are crushed in spirit.", ref: "Psalm 34:18" },
  { verse: "And my God will meet all your needs according to the riches of his glory in Christ Jesus.", ref: "Philippians 4:19" },
  { verse: "Be strong and courageous. Do not be afraid; do not be discouraged.", ref: "Joshua 1:9" }
];

export default function TestimoniesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [testimonies, setTestimonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [scripture, setScripture] = useState('');
  const [scriptureRef, setScriptureRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Report dialog state
  const [reportTarget, setReportTarget] = useState(null); // { testimony_id, user_name }
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);

  // Share dialog state
  const [shareTarget, setShareTarget] = useState(null); // testimony object

  useEffect(() => {
    loadTestimonies();
  }, []);

  const loadTestimonies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/testimonies`, { withCredentials: true });
      setTestimonies(data);
    } catch (e) {
      console.error('Failed to load testimonies:', e);
    } finally {
      setLoading(false);
    }
  };

  const openReport = (testimony) => {
    setReportTarget({ testimony_id: testimony.testimony_id, user_name: testimony.user_name || 'Anonymous' });
    setReportReason('');
    setReportDetails('');
  };

  const submitReport = async () => {
    if (!reportTarget) return;
    if (!reportReason) {
      toast.error('Please choose a reason');
      return;
    }
    setReporting(true);
    try {
      await axios.post(
        `${API_URL}/api/testimonies/${reportTarget.testimony_id}/report`,
        { reason: reportReason, details: reportDetails.trim() || null },
        { withCredentials: true }
      );
      // Optimistically hide it from this user's view
      setTestimonies((prev) => prev.filter((t) => t.testimony_id !== reportTarget.testimony_id));
      toast.success('Thank you — our team will review this testimony.');
      setReportTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit the report. Please try again.');
    } finally {
      setReporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/testimonies`, {
        message: message.trim(),
        scripture: scripture.trim() || null,
        scripture_ref: scriptureRef.trim() || null
      }, { withCredentials: true });
      setSubmitted(true);
      setMessage('');
      setScripture('');
      setScriptureRef('');
      setShowForm(false);
      loadTestimonies();
      setTimeout(() => setSubmitted(false), 4000);
    } catch (e) {
      console.error('Failed to submit testimony:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const selectVerse = (v) => {
    setScripture(v.verse);
    setScriptureRef(v.ref);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-testid="testimonies-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70 hover:text-[#36454F]">Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">
            Testimony Wall
          </h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            Stories of faith, freedom, and financial victory
          </p>
        </div>

        {/* Share Your Story */}
        {submitted && (
          <div className="mb-6 p-4 bg-[#4CAF50]/10 border border-[#4CAF50]/30 text-[#4CAF50] font-['Manrope'] text-sm flex items-center animate-fade-in-up" data-testid="testimony-success">
            <Heart className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Your testimony has been shared. Thank you for encouraging others!
          </div>
        )}

        {!showForm ? (
          <Card className="mb-8 border-[#E0BFB8] bg-[#E0BFB8]/10 animate-fade-in-up">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 text-[#B76E79] mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                Share Your Testimony
              </h3>
              <p className="text-sm text-[#36454F]/65 font-['Manrope'] mb-4 max-w-md mx-auto">
                Your story of overcoming debt or improving your credit can inspire someone else on their journey.
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="share-testimony-btn">
                <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />Share Your Story
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-8 border-[#36454F]/15 bg-white animate-fade-in-up">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                <Heart className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                Share Your Story
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Your Testimony</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Share how God has helped you on your financial journey..."
                    className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] min-h-[120px]"
                    required
                    data-testid="testimony-message-input"
                  />
                  <p className="mt-1 text-xs text-[#36454F]/40 font-['Manrope']">
                    Only your first name will be shown. Your story is anonymous.
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">
                    Attach a Scripture (optional)
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2 mb-3">
                    {SUGGESTED_VERSES.map((v, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectVerse(v)}
                        className={`text-xs px-3 py-1.5 rounded-sm border font-['Manrope'] transition-all ${
                          scriptureRef === v.ref
                            ? 'bg-[#B76E79] text-white border-[#B76E79]'
                            : 'bg-[#F5F5F5] text-[#36454F]/70 border-[#36454F]/10 hover:border-[#B76E79]'
                        }`}
                        data-testid={`verse-chip-${i}`}
                      >
                        {v.ref}
                      </button>
                    ))}
                  </div>
                  <Input
                    value={scripture}
                    onChange={(e) => setScripture(e.target.value)}
                    placeholder="Or type your own verse..."
                    className="border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm"
                    data-testid="testimony-scripture-input"
                  />
                  <Input
                    value={scriptureRef}
                    onChange={(e) => setScriptureRef(e.target.value)}
                    placeholder="Reference (e.g., Romans 8:28)"
                    className="mt-2 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] text-sm"
                    data-testid="testimony-ref-input"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button type="submit" disabled={submitting || !message.trim()} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="submit-testimony-btn">
                    {submitting ? 'Sharing...' : <><Send className="w-4 h-4 mr-2" strokeWidth={1.5} />Share Testimony</>}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="text-[#36454F]/50 font-['Manrope']">Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Testimonies List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        ) : testimonies.length === 0 ? (
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
            <CardContent className="p-12 text-center">
              <MessageSquare className="w-16 h-16 text-[#36454F]/15 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">
                Be the first to share
              </h3>
              <p className="text-[#36454F]/65 font-['Manrope'] max-w-md mx-auto">
                Your testimony could be the encouragement someone needs today.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {testimonies.map((testimony, index) => (
              <Card key={testimony.testimony_id} className="border-[#36454F]/15 bg-white animate-fade-in-up" style={{ animationDelay: `${index * 80}ms` }} data-testid={`testimony-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-full bg-[#E0BFB8]/40 flex items-center justify-center flex-shrink-0">
                      <span className="font-['Playfair_Display'] text-[#B76E79] font-semibold">
                        {testimony.user_name?.[0]?.toUpperCase() || 'A'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-['Manrope'] font-medium text-[#36454F]">
                            {testimony.user_name || 'Anonymous'}
                          </span>
                          <span className="text-xs text-[#36454F]/30 font-['Manrope']">
                            {new Date(testimony.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => openReport(testimony)}
                          className="text-[11px] text-[#36454F]/40 hover:text-[#D9534F] font-['Manrope'] inline-flex items-center gap-1 transition-colors"
                          title="Report this testimony"
                          data-testid={`report-testimony-btn-${index}`}
                        >
                          <Flag className="w-3 h-3" strokeWidth={1.5} />
                          Report
                        </button>
                      </div>
                      <p className="text-[#36454F]/80 font-['Manrope'] leading-relaxed mb-3">
                        {testimony.message}
                      </p>
                      {testimony.scripture && (
                        <div className="p-3 bg-[#E0BFB8]/10 border-l-3 border-l-[#B76E79] rounded-r-sm">
                          <p className="font-['Playfair_Display'] text-sm italic text-[#36454F]">
                            "{testimony.scripture}"
                          </p>
                          {testimony.scripture_ref && (
                            <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1">
                              — {testimony.scripture_ref}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Report Testimony Dialog (Google Play UGC policy requirement) */}
      <Dialog open={!!reportTarget} onOpenChange={(open) => !open && setReportTarget(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="report-testimony-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-[#36454F] flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#D9534F]" strokeWidth={1.5} />
              Report this testimony
            </DialogTitle>
            <DialogDescription className="font-['Manrope'] text-[#36454F]/65">
              Help us keep Selah Slate safe and encouraging. Our team reviews every report.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Reason</Label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger
                  className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                  data-testid="report-reason-select"
                >
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="spam" className="font-['Manrope']">Spam or misleading</SelectItem>
                  <SelectItem value="harassment" className="font-['Manrope']">Harassment or hate</SelectItem>
                  <SelectItem value="inappropriate" className="font-['Manrope']">Inappropriate or explicit content</SelectItem>
                  <SelectItem value="misinformation" className="font-['Manrope']">Financial misinformation</SelectItem>
                  <SelectItem value="other" className="font-['Manrope']">Something else</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-['Manrope'] text-[#36454F]/80">
                Additional details <span className="text-[#36454F]/40">(optional)</span>
              </Label>
              <Textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value.slice(0, 500))}
                placeholder="Anything specific you'd like our reviewers to know"
                className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] min-h-[80px]"
                data-testid="report-details-input"
              />
              <p className="mt-1 text-[10px] text-[#36454F]/40 font-['Manrope'] text-right">
                {reportDetails.length}/500
              </p>
            </div>

            <p className="text-[11px] text-[#36454F]/55 font-['Manrope']">
              Once submitted, this testimony will be hidden from your view immediately while our team reviews it.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setReportTarget(null)}
              className="text-[#36454F]/60 font-['Manrope']"
              data-testid="report-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReport}
              disabled={reporting || !reportReason}
              className="bg-[#D9534F] hover:bg-[#c1413d] text-white font-['Manrope'] rounded-sm disabled:opacity-50"
              data-testid="report-submit-btn"
            >
              {reporting ? 'Sending…' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AppFooter />
    </div>
  );
}
